import { useState } from "react";
import { type UserStory } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, GripVertical, X, ChevronDown, ChevronUp, Calculator } from "lucide-react";

const priorityColors: Record<UserStory["priority"], string> = {
  must: "bg-destructive/15 text-destructive border-destructive/30",
  should: "bg-accent/15 text-accent border-accent/30",
  could: "bg-secondary text-secondary-foreground border-border",
  wont: "bg-muted text-muted-foreground border-border",
};

const priorityLabels: Record<UserStory["priority"], string> = {
  must: "Must Have",
  should: "Should Have",
  could: "Could Have",
  wont: "Won't Have",
};

const statusColors: Record<UserStory["status"], string> = {
  backlog: "bg-muted text-muted-foreground",
  "in-progress": "bg-accent/15 text-accent",
  done: "bg-primary/15 text-primary",
};

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
}

function computeRice(reach: number, impact: number, confidence: number, effort: number): number {
  if (!effort || effort <= 0) return 0;
  return Math.round((reach * impact * (confidence / 100)) / effort);
}

const IMPACT_OPTIONS = [
  { value: 3, label: "3 — Massive" },
  { value: 2, label: "2 — High" },
  { value: 1, label: "1 — Medium" },
  { value: 0.5, label: "0.5 — Low" },
  { value: 0.25, label: "0.25 — Minimal" },
];

const EMPTY_FORM = {
  title: "", asA: "", iWant: "", soThat: "",
  priority: "should" as UserStory["priority"],
  storyPoints: 3,
  criteria: "",
  rejectionRationale: "",
  showRice: false,
  riceReach: 100,
  riceImpact: 1,
  riceConfidence: 80,
  riceEffort: 1,
};

export default function UserStoryEditor({ stories, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const openAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (story: UserStory) => {
    setEditingId(story.id);
    setForm({
      title: story.title,
      asA: story.asA || "",
      iWant: story.iWant || "",
      soThat: story.soThat || "",
      priority: story.priority,
      storyPoints: story.storyPoints || 3,
      criteria: (story.acceptanceCriteria || []).join("\n"),
      rejectionRationale: story.rejectionRationale || "",
      showRice: !!story.riceScore,
      riceReach: story.riceScore?.reach ?? 100,
      riceImpact: story.riceScore?.impact ?? 1,
      riceConfidence: story.riceScore?.confidence ?? 80,
      riceEffort: story.riceScore?.effort ?? 1,
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    const criteria = form.criteria.split("\n").map(c => c.trim()).filter(Boolean);
    const riceScore = form.showRice ? {
      reach: form.riceReach,
      impact: form.riceImpact,
      confidence: form.riceConfidence,
      effort: form.riceEffort,
    } : undefined;

    if (editingId) {
      onChange(stories.map(s => s.id === editingId ? {
        ...s,
        title: form.title.trim(),
        asA: form.asA.trim(),
        iWant: form.iWant.trim(),
        soThat: form.soThat.trim(),
        acceptanceCriteria: criteria,
        priority: form.priority,
        storyPoints: form.storyPoints,
        riceScore,
        rejectionRationale: form.priority === "wont" ? form.rejectionRationale : undefined,
      } : s));
    } else {
      const story: UserStory = {
        id: crypto.randomUUID(),
        title: form.title.trim(),
        asA: form.asA.trim(),
        iWant: form.iWant.trim(),
        soThat: form.soThat.trim(),
        acceptanceCriteria: criteria,
        priority: form.priority,
        storyPoints: form.storyPoints,
        status: "backlog",
        riceScore,
        rejectionRationale: form.priority === "wont" ? form.rejectionRationale : undefined,
      };
      onChange([...stories, story]);
    }
    setForm(EMPTY_FORM);
    setOpen(false);
  };

  const updateStoryStatus = (id: string, status: UserStory["status"]) => {
    onChange(stories.map((s) => (s.id === id ? { ...s, status } : s)));
  };

  const removeStory = (id: string) => {
    onChange(stories.filter((s) => s.id !== id));
  };

  const grouped = {
    must: stories.filter((s) => s.priority === "must"),
    should: stories.filter((s) => s.priority === "should"),
    could: stories.filter((s) => s.priority === "could"),
    wont: stories.filter((s) => s.priority === "wont"),
  };

  const ricePreview = form.showRice
    ? computeRice(form.riceReach, form.riceImpact, form.riceConfidence, form.riceEffort)
    : null;

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            User Stories
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            MoSCoW prioritized · {stories.length} total ·{" "}
            {stories.reduce((a, s) => a + (s.storyPoints || 0), 0)} pts
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" onClick={openAdd}>
              <Plus className="h-3.5 w-3.5" /> Add Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-display">{editingId ? "Edit Story" : "New User Story"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input
                placeholder="Story title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="As a…"
                  value={form.asA}
                  onChange={(e) => setForm({ ...form, asA: e.target.value })}
                />
                <Input
                  placeholder="I want…"
                  value={form.iWant}
                  onChange={(e) => setForm({ ...form, iWant: e.target.value })}
                />
                <Input
                  placeholder="So that…"
                  value={form.soThat}
                  onChange={(e) => setForm({ ...form, soThat: e.target.value })}
                />
              </div>
              <Textarea
                placeholder="Acceptance criteria (one per line)"
                value={form.criteria}
                onChange={(e) => setForm({ ...form, criteria: e.target.value })}
                rows={3}
              />
              <div className="flex gap-3">
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v as any })}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="must">Must Have</SelectItem>
                    <SelectItem value="should">Should Have</SelectItem>
                    <SelectItem value="could">Could Have</SelectItem>
                    <SelectItem value="wont">Won't Have</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  max={21}
                  value={form.storyPoints}
                  onChange={(e) => setForm({ ...form, storyPoints: +e.target.value })}
                  className="w-20"
                  placeholder="Pts"
                />
              </div>

              {/* Rejection rationale — visible only for Won't Have */}
              {form.priority === "wont" && (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">
                    Rejection Rationale
                  </label>
                  <Textarea
                    placeholder="Why was this deprioritised? What tradeoff was made? (e.g. 'Removed to reduce scope — doesn't serve the core user need in v1')"
                    value={form.rejectionRationale}
                    onChange={(e) => setForm({ ...form, rejectionRationale: e.target.value })}
                    rows={2}
                    className="text-sm"
                  />
                </div>
              )}

              {/* RICE Scoring — collapsible */}
              <div className="border border-border rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, showRice: !form.showRice })}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  <span className="flex items-center gap-1.5 font-mono uppercase tracking-wider">
                    <Calculator className="h-3.5 w-3.5" />
                    RICE Score
                    {form.showRice && ricePreview !== null && (
                      <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 font-bold">
                        {ricePreview}
                      </Badge>
                    )}
                  </span>
                  {form.showRice ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>

                {form.showRice && (
                  <div className="px-3 pb-3 pt-2 space-y-3 bg-muted/20">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-mono text-muted-foreground block mb-1">
                          Reach (users/period)
                        </label>
                        <Input
                          type="number"
                          min={1}
                          value={form.riceReach}
                          onChange={(e) => setForm({ ...form, riceReach: +e.target.value || 1 })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-mono text-muted-foreground block mb-1">
                          Impact
                        </label>
                        <Select
                          value={String(form.riceImpact)}
                          onValueChange={(v) => setForm({ ...form, riceImpact: parseFloat(v) })}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {IMPACT_OPTIONS.map(o => (
                              <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] font-mono text-muted-foreground block mb-1">
                          Confidence (%)
                        </label>
                        <Input
                          type="number"
                          min={10}
                          max={100}
                          step={10}
                          value={form.riceConfidence}
                          onChange={(e) => setForm({ ...form, riceConfidence: Math.min(100, Math.max(10, +e.target.value || 10)) })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-mono text-muted-foreground block mb-1">
                          Effort (person-months)
                        </label>
                        <Input
                          type="number"
                          min={0.25}
                          step={0.25}
                          value={form.riceEffort}
                          onChange={(e) => setForm({ ...form, riceEffort: Math.max(0.25, +e.target.value || 0.25) })}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                    {ricePreview !== null && (
                      <div className="flex items-center justify-between text-[11px] font-mono bg-background rounded px-2 py-1.5 border border-border">
                        <span className="text-muted-foreground">
                          ({form.riceReach} × {form.riceImpact} × {form.riceConfidence}%) ÷ {form.riceEffort}
                        </span>
                        <span className="font-bold text-foreground">= {ricePreview}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button onClick={handleSave} className="w-full">
                {editingId ? "Save Changes" : "Add Story"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No user stories yet. Click "Add Story" to create your first one.
        </div>
      ) : (
        Object.entries(grouped).map(([priority, items]) => {
          if (items.length === 0) return null;
          return (
            <div key={priority}>
              <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <Badge variant="outline" className={priorityColors[priority as UserStory["priority"]]}>
                  {priorityLabels[priority as UserStory["priority"]]}
                </Badge>
                <span className="text-muted-foreground/50">({items.length})</span>
              </h3>
              <div className="space-y-2">
                {items.map((story) => {
                  const rice = story.riceScore
                    ? computeRice(story.riceScore.reach, story.riceScore.impact, story.riceScore.confidence, story.riceScore.effort)
                    : null;
                  return (
                    <div
                      key={story.id}
                      className="border border-border rounded-md p-3 bg-card flex items-start gap-3 cursor-pointer hover:border-muted-foreground/40 transition-colors"
                      onClick={() => openEdit(story)}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground/30 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold text-card-foreground font-body">
                            {story.title}
                          </span>
                          {story.storyPoints && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {story.storyPoints} pts
                            </Badge>
                          )}
                          {rice !== null && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800 gap-0.5">
                              <Calculator className="h-2.5 w-2.5" />
                              {rice}
                            </Badge>
                          )}
                        </div>
                        {story.asA && (
                          <p className="text-xs text-muted-foreground font-body">
                            As a <span className="font-semibold">{story.asA}</span>, I want{" "}
                            <span className="font-semibold">{story.iWant}</span>, so that{" "}
                            <span className="font-semibold">{story.soThat}</span>
                          </p>
                        )}
                        {story.priority === "wont" && story.rejectionRationale && (
                          <p className="text-[11px] text-muted-foreground mt-1 italic border-l-2 border-muted-foreground/20 pl-2">
                            ⊘ {story.rejectionRationale}
                          </p>
                        )}
                        {story.acceptanceCriteria.length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {story.acceptanceCriteria.map((c, i) => (
                              <p key={i} className="text-[11px] text-muted-foreground font-mono">
                                ✓ {c}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                        <Select
                          value={story.status}
                          onValueChange={(v) => updateStoryStatus(story.id, v as any)}
                        >
                          <SelectTrigger className="h-7 text-[10px] w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="backlog">Backlog</SelectItem>
                            <SelectItem value="in-progress">In Progress</SelectItem>
                            <SelectItem value="done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => removeStory(story.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
